/** Desktop-side ElevenLabs + OpenAI TTS when API keys are present in Electron env. */

export const DESKTOP_ELEVENLABS_VOICES = [
  { id: "JBFqnCBsd6RMkjVDRZzb", name: "George" },
  { id: "N2lVS1w4EtoT3dr4eOWO", name: "Callum" },
  { id: "TX3LPaxmHKxFdv7VOQHJ", name: "Liam" },
  { id: "XB0fDUnXU5powFXDhCwa", name: "Charlotte" },
  { id: "Xb7hH8MSUJpSbSDYk0k2", name: "Alice" },
  { id: "onwK4e9ZLuTAKqWW03F9", name: "Daniel" },
  { id: "pqHfZKP75CvOlQylNhV4", name: "Bill" },
  { id: "nPczCjzI2devNBz1zQrb", name: "Brian" },
] as const;

export const DESKTOP_OPENAI_VOICES = [
  { id: "alloy", name: "Alloy" },
  { id: "echo", name: "Echo" },
  { id: "fable", name: "Fable" },
  { id: "onyx", name: "Onyx" },
  { id: "nova", name: "Nova" },
  { id: "shimmer", name: "Shimmer" },
] as const;

const ELEVENLABS_VOICE_IDS = new Set<string>(DESKTOP_ELEVENLABS_VOICES.map((v) => v.id));
const OPENAI_VOICE_IDS = new Set<string>(DESKTOP_OPENAI_VOICES.map((v) => v.id));
const ELEVENLABS_MAX_CHUNK = 40_000;
const OPENAI_MAX_CHUNK = 4_000;
const ELEVENLABS_MODEL = "eleven_flash_v2_5";
const ELEVENLABS_OUTPUT = "mp3_44100_128";

export type DesktopCloudTtsProvider = "elevenlabs" | "openai";

export type DesktopCloudTtsVoice = {
  id: string;
  name: string;
  provider: DesktopCloudTtsProvider;
};

export type DesktopCloudTtsResult = {
  audio: ArrayBuffer;
  contentType: "audio/mpeg";
  provider: DesktopCloudTtsProvider;
  voice: string;
};

export function getDesktopElevenLabsApiKey(): string | null {
  return process.env.ELEVENLABS_API_KEY?.trim() || null;
}

export function getDesktopOpenAiApiKey(): string | null {
  return process.env.OPENAI_API_KEY?.trim() || null;
}

export function isDesktopElevenLabsVoice(id: string): boolean {
  return ELEVENLABS_VOICE_IDS.has(id);
}

export function isDesktopOpenAiVoice(id: string): boolean {
  return OPENAI_VOICE_IDS.has(id);
}

/**
 * True for catalog ElevenLabs ids and user-pasted voice ids from the ElevenLabs dashboard.
 * Excludes OpenAI short names and local/Kokoro ids (often contain `_`).
 */
export function looksLikeElevenLabsVoiceId(id: string): boolean {
  const t = id.trim();
  if (!t || OPENAI_VOICE_IDS.has(t)) return false;
  if (t.includes("_") || t.includes("-") || t.includes(" ")) return false;
  return /^[a-zA-Z0-9]{16,64}$/.test(t);
}

/** Catalog or custom voice id that desktop can synthesize with ElevenLabs. */
export function canUseDesktopElevenLabsVoice(id: string): boolean {
  if (!getDesktopElevenLabsApiKey()) return false;
  const t = id.trim();
  return isDesktopElevenLabsVoice(t) || looksLikeElevenLabsVoiceId(t);
}

export function formatCustomElevenLabsVoiceLabel(id: string): string {
  const t = id.trim();
  if (!t) return "Custom voice";
  const named = DESKTOP_ELEVENLABS_VOICES.find((v) => v.id === t);
  if (named) return named.name;
  return t.length > 12 ? `Custom (${t.slice(0, 6)}…${t.slice(-4)})` : `Custom (${t})`;
}

export function listDesktopCloudTtsVoices(): DesktopCloudTtsVoice[] {
  const voices: DesktopCloudTtsVoice[] = [];
  if (getDesktopElevenLabsApiKey()) {
    for (const v of DESKTOP_ELEVENLABS_VOICES) {
      voices.push({ id: v.id, name: v.name, provider: "elevenlabs" });
    }
  }
  if (getDesktopOpenAiApiKey()) {
    for (const v of DESKTOP_OPENAI_VOICES) {
      voices.push({ id: v.id, name: v.name, provider: "openai" });
    }
  }
  return voices;
}

function splitTextIntoChunks(text: string, maxSize: number): string[] {
  if (text.length <= maxSize) return [text];
  const chunks: string[] = [];
  let current = "";
  for (const sentence of text.split(/(?<=[.!?])\s+/)) {
    if (sentence.length > maxSize) {
      if (current.trim()) {
        chunks.push(current.trim());
        current = "";
      }
      for (const clause of sentence.split(/(?<=,)\s*/)) {
        if ((current + clause).length > maxSize && current.trim()) {
          chunks.push(current.trim());
          current = clause + " ";
        } else {
          current += clause + " ";
        }
      }
    } else if ((current + sentence).length > maxSize) {
      chunks.push(current.trim());
      current = sentence + " ";
    } else {
      current += sentence + " ";
    }
  }
  if (current.trim()) chunks.push(current.trim());
  return chunks;
}

function concatenateArrayBuffers(parts: ArrayBuffer[]): ArrayBuffer {
  const total = parts.reduce((sum, part) => sum + part.byteLength, 0);
  const out = new Uint8Array(total);
  let offset = 0;
  for (const part of parts) {
    out.set(new Uint8Array(part), offset);
    offset += part.byteLength;
  }
  return out.buffer;
}

async function synthesizeElevenLabsChunk(
  apiKey: string,
  voiceId: string,
  text: string,
  previousRequestIds: string[]
): Promise<{ audio: ArrayBuffer; requestId?: string }> {
  const url = new URL(`https://api.elevenlabs.io/v1/text-to-speech/${encodeURIComponent(voiceId)}`);
  url.searchParams.set("output_format", ELEVENLABS_OUTPUT);
  const response = await fetch(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: "audio/mpeg",
      "xi-api-key": apiKey,
    },
    body: JSON.stringify({
      text,
      model_id: ELEVENLABS_MODEL,
      previous_request_ids: previousRequestIds.length ? previousRequestIds : undefined,
    }),
  });
  if (!response.ok) {
    const errText = await response.text().catch(() => "");
    throw new Error(errText.trim() || `ElevenLabs request failed (${response.status})`);
  }
  const audio = await response.arrayBuffer();
  const requestId = response.headers.get("request-id") ?? undefined;
  return { audio, requestId };
}

async function generateElevenLabsAudio(text: string, voiceId: string): Promise<DesktopCloudTtsResult> {
  const apiKey = getDesktopElevenLabsApiKey();
  if (!apiKey) throw new Error("ELEVENLABS_API_KEY is not configured");
  const chunks = splitTextIntoChunks(text, ELEVENLABS_MAX_CHUNK);
  const audioChunks: ArrayBuffer[] = [];
  const previousRequestIds: string[] = [];
  for (let i = 0; i < chunks.length; i++) {
    const { audio, requestId } = await synthesizeElevenLabsChunk(
      apiKey,
      voiceId,
      chunks[i]!,
      previousRequestIds
    );
    audioChunks.push(audio);
    if (requestId) {
      previousRequestIds.push(requestId);
      if (previousRequestIds.length > 3) previousRequestIds.shift();
    }
  }
  return {
    audio: concatenateArrayBuffers(audioChunks),
    contentType: "audio/mpeg",
    provider: "elevenlabs",
    voice: voiceId,
  };
}

async function generateOpenAiAudio(text: string, voiceId: string): Promise<DesktopCloudTtsResult> {
  const apiKey = getDesktopOpenAiApiKey();
  if (!apiKey) throw new Error("OPENAI_API_KEY is not configured");
  if (!isDesktopOpenAiVoice(voiceId)) throw new Error(`Unknown OpenAI voice: ${voiceId}`);
  const chunks = splitTextIntoChunks(text, OPENAI_MAX_CHUNK);
  const audioChunks: ArrayBuffer[] = [];
  for (let i = 0; i < chunks.length; i++) {
    const response = await fetch("https://api.openai.com/v1/audio/speech", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${apiKey}`,
      },
      body: JSON.stringify({
        model: "tts-1",
        input: chunks[i],
        voice: voiceId,
        response_format: "mp3",
      }),
    });
    if (!response.ok) {
      const err = (await response.json().catch(() => ({}))) as { error?: { message?: string } };
      throw new Error(err.error?.message || `OpenAI TTS failed: ${response.status}`);
    }
    audioChunks.push(await response.arrayBuffer());
    if (i < chunks.length - 1) {
      await new Promise((resolve) => setTimeout(resolve, 200));
    }
  }
  return {
    audio: concatenateArrayBuffers(audioChunks),
    contentType: "audio/mpeg",
    provider: "openai",
    voice: voiceId,
  };
}

export async function generateDesktopCloudTtsAudio(
  text: string,
  voiceId: string
): Promise<DesktopCloudTtsResult | null> {
  const id = voiceId.trim();
  if (canUseDesktopElevenLabsVoice(id)) {
    return generateElevenLabsAudio(text, id);
  }
  if (isDesktopOpenAiVoice(id) && getDesktopOpenAiApiKey()) {
    return generateOpenAiAudio(text, id);
  }
  return null;
}
